import { Sites } from '@ps/platform-message-framework/common/Sites';
import { ErrorCode, ResolverTag } from '@ps/web-core-enums';
import { useTrackers } from '@ps/web-shared-analytics';
import {
  StarsButton as Button,
  PowMessage,
  Translation,
  useTranslationValues
} from '@ps/web-shared-components';
import { StarsInfo, starsEmailLink, translatedLink } from '@ps/web-shared-ui';
import { get } from 'lodash';
import React, { useEffect } from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { styles as sharedStyles } from '@swf/account';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import { Country } from '@swf/core/enums';
import { StatusCode, StatusSubcode } from '@swf/core/enums/statusCode';
import { select } from '@swf/core/store';
import { enumCase, statusKey } from '@swf/core/utils';
import { SelfExcludedBrandsList, ServerMessageHandler } from '@swf/notification';

import {
  TXTCLI_If_you_require_further_assista_ELL_7,
  TXTCLI_In_accordance_with_Greek_Gambl_ELL,
  TXTCLI_In_accordance_with_Greek_Gambl_ELL_1,
  TXTCLI_No_Account_Found,
  TXTCLI_Please_note_that_your_account__ELL_3,
  TXTCLI_Please_note_that_your_account__ELL_4,
  TXTCLI_Something_went_wrongX,
  TXTCLI_The_PIN_you_have_entered_is_in_ELL,
  TXTCLI_The_account_cannot_be_re_activ_ELL,
  TXTCLI_We_apologize_for_any_inconveni_ELL_1,
  TXTCLI_Xa_hrefXXN0XXLoginX_aX_to_N1_u_ELL,
  TXTCLI_Your_account_balance_will_be_c_ELL,
  TXTMOB_Something_went_wrongX_Please_t_ELL,
  TXTMOB_Your_email_address_was_used_to_ELL,
  TXTWCL_Currently_email_address_cannot_ELL,
  TXTWCL_For_more_information_please_vi_ELL_2,
  TXTWCL_Invalid_username_or_passwordX,
  TXTWCL_Learn_more_about_Xa_hrefXXN0XX_ELL,
  TXTWCL_Please_try_again_later_or_visi_ELL,
  TXTWCL_SorryX_we_canXt_process_your_r_ELL,
  TXTWCL_The_RSA_Passcode_you_have_ente_ELL,
  TXTWCL_The_User_ID_and_Password_combi_ELL_1,
  TXTWCL_The_XXappXdomainXX_client_is_n_ELL,
  TXTWCL_Visit_N0,
  TXTWCL_We_are_unable_to_log_you_into__ELL,
  TXTWCL_When_you_self_excludeX_we_prot_ELL,
  TXTWCL_You_wonXt_be_able_to_use_any_o_ELL_1,
  TXTWCL_Your_N0_account_can_only_be_us_ELL,
  TXTWCL_Your_N0_account_can_only_be_us_ELL_1,
  TXTWCL_Your_XXAccountBrandXX_Account__ELL_4,
  TXTWCL_Your_account_has_been_closedX__ELL
} from './ErrorMessage.messages.json';
import styles from './ErrorMessage.scss';

const navigateToUserSite = (usersite: string): void => {
  window.location.href = `//${usersite}`;
};

const ErrorMessage: React.FC = () => {
  const {
    fieldErrors,
    globalErrors,
    serverMessage,
    statusCode,
    statusSubcode,
    userSite,
    client,
    country,
    errorCode
  } = useSelector(select.auth.getResponse);
  const domain = useSelector(select.cms.getDomain);
  const site = useSelector(select.cms.getSiteId);
  const history = useHistory();
  const { brand } = useTranslationValues();
  const { errorViewed } = useTrackers();
  const {
    input: { value: userId }
  } = useField('userId');

  const fieldError = get(fieldErrors, [0, 'fieldName']);
  const globalError = get(globalErrors, [0, 'code']);

  useEffect(() => {
    if (fieldError) {
      errorViewed({ errorCode: enumCase(fieldError) });
    } else if (statusCode) {
      errorViewed({ errorCode: statusKey(statusCode) });
    }
  }, [fieldError, errorViewed, statusCode]);

  switch (true) {
    case !statusCode && !errorCode:
      return null;
    case fieldError === 'rsaCode':
      return (
        <p className={styles.error}>
          <Translation {...TXTWCL_The_RSA_Passcode_you_have_ente_ELL} />
        </p>
      );
    case fieldError === 'pin':
      return (
        <p className={styles.error}>
          <Translation {...TXTCLI_The_PIN_you_have_entered_is_in_ELL} />
        </p>
      );
    case fieldError === 'email':
      return (
        <p className={styles.error}>
          <Translation {...TXTMOB_Your_email_address_was_used_to_ELL} />{' '}
          <Translation
            {...TXTCLI_Xa_hrefXXN0XXLoginX_aX_to_N1_u_ELL}
            values={{
              1: brand,
              a: translatedLink({
                href: '/auth/login',
                onClick: (e) => {
                  e.preventDefault();
                  history.push('/auth/login');
                }
              })
            }}
          />
        </p>
      );
    case statusCode === StatusCode.POW_REQUIRED:
    case errorCode === ErrorCode.POW_REQUIRED:
    case globalError === ErrorCode.POW_INVALID:
      return <PowMessage className={styles.error} />;
    case statusCode === StatusCode.LOGIN_INVALID_CREDENTIALS:
    case errorCode === ErrorCode.INVALID_CREDENTIALS:
      return (
        <p className={styles.error}>
          <Translation {...TXTWCL_The_User_ID_and_Password_combi_ELL_1} />
        </p>
      );
    case errorCode === ErrorCode.LOGIN_BLOCKED_COOL_OFF:
      return (
        <StarsInfo type="error" className={styles.error}>
          <Translation {...TXTWCL_Invalid_username_or_passwordX} />{' '}
          <Translation
            {...TXTWCL_Please_try_again_later_or_visi_ELL}
            values={{
              a: (msg: React.ReactNode) => (
                <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
              )
            }}
          />
        </StarsInfo>
      );
    case statusCode === StatusCode.LOGIN_NOT_ALLOWED:
    case errorCode === ErrorCode.LOGIN_MSG_NOT_ALLOWED:
      switch (site) {
        case Sites.GR:
          return (
            <StarsInfo type="error" className={styles.error}>
              <Translation tagName="p" {...TXTCLI_In_accordance_with_Greek_Gambl_ELL_1} />
              <Translation
                tagName="p"
                values={{ 0: 3 }}
                {...TXTCLI_Your_account_balance_will_be_c_ELL}
              />
              <Translation
                values={{ a: starsEmailLink }}
                {...TXTCLI_If_you_require_further_assista_ELL_7}
              />
            </StarsInfo>
          );
        case Sites.POR:
          return (
            <StarsInfo type="error" className={styles.error}>
              <Translation tagName="p" {...TXTCLI_Please_note_that_your_account__ELL_3} />
              <Translation {...TXTCLI_The_account_cannot_be_re_activ_ELL} />
            </StarsInfo>
          );
        default:
          return (
            <p className={styles.error}>
              <Translation
                {...TXTWCL_Your_XXAccountBrandXX_Account__ELL_4}
                values={{
                  a: (msg: React.ReactNode) => (
                    <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
                  )
                }}
              />
            </p>
          );
      }
    case statusCode === StatusCode.PIN_INVALID:
    case errorCode === ErrorCode.PIN_INVALID:
      return (
        <p className={styles.error}>
          <Translation {...TXTCLI_The_PIN_you_have_entered_is_in_ELL} />
        </p>
      );
    case statusCode === StatusCode.EMAIL_NOT_ALLOWED:
    case errorCode === ErrorCode.EMAIL_NOT_ALLOWED:
      return (
        <p className={styles.error}>
          <Translation {...TXTWCL_Currently_email_address_cannot_ELL} />
        </p>
      );
    case statusCode === StatusCode.ACCOUNT_CLOSED &&
      statusSubcode === StatusSubcode.AUTH_SUB_ERROR_ACCOUNT_CLOSED_2:
    case errorCode === ErrorCode.ACCOUNT_CLOSED:
      if (site === Sites.SPA) {
        return (
          <div className={styles.error}>
            <Translation tagName="p" {...TXTCLI_Please_note_that_your_account__ELL_4} />
            <Translation {...TXTCLI_The_account_cannot_be_re_activ_ELL} />
          </div>
        );
      }
      if (site === Sites.GR) {
        return (
          <StarsInfo type="error" className={styles.error}>
            <Translation tagName="p" {...TXTCLI_In_accordance_with_Greek_Gambl_ELL} />
            <Translation
              values={{ a: starsEmailLink }}
              {...TXTCLI_If_you_require_further_assista_ELL_7}
            />
          </StarsInfo>
        );
      }
      if (site === Sites.UK) {
        return (
          <StarsInfo type="error" className={styles.error}>
            <Translation tagName="p" {...TXTWCL_SorryX_we_canXt_process_your_r_ELL} />
            <Translation tagName="p" {...TXTWCL_When_you_self_excludeX_we_prot_ELL} />
            <SelfExcludedBrandsList />
            <Translation tagName="p" {...TXTWCL_You_wonXt_be_able_to_use_any_o_ELL_1} />
            <Translation
              tagName="p"
              {...TXTWCL_For_more_information_please_vi_ELL_2}
              values={{
                a: (msg: React.ReactNode) => (
                  <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
                )
              }}
            />
            <Translation
              tagName="p"
              {...TXTWCL_Learn_more_about_Xa_hrefXXN0XX_ELL}
              values={{
                a: (msg: React.ReactNode) => (
                  <ResolverLink resolverTag={ResolverTag.HelpResponsibleGaming}>{msg}</ResolverLink>
                )
              }}
            />
          </StarsInfo>
        );
      }

      return (
        <p className={styles.error}>
          <ServerMessageHandler message={serverMessage as string} />
        </p>
      );
    case statusCode === StatusCode.ACCOUNT_CLOSED_PV:
    case errorCode === ErrorCode.ACCOUNT_CLOSED_PV:
      return (
        <p className={styles.error}>
          <Translation
            {...TXTWCL_Your_XXAccountBrandXX_Account__ELL_4}
            values={{
              a: (msg: React.ReactNode) => (
                <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
              )
            }}
          />
        </p>
      );
    case statusCode === StatusCode.SITE_MISMATCH:
    case errorCode === ErrorCode.SITE_MISMATCH:
      return (
        <>
          <StarsInfo type="error" className={sharedStyles.error}>
            <Translation
              tagName="span"
              {...TXTWCL_Your_N0_account_can_only_be_us_ELL_1}
              values={{
                '0': userId,
                '1': client,
                '2': userSite
              }}
            />
          </StarsInfo>
          <Button
            block
            onClick={() => userSite && navigateToUserSite(userSite)}
            translation={TXTWCL_Visit_N0}
            values={{ '0': userSite }}
          />
        </>
      );
    case statusCode === StatusCode.LICENSE_MISMATCH:
    case errorCode === ErrorCode.LICENSE_MISMATCH:
      return (
        <>
          <StarsInfo type="error" className={sharedStyles.error}>
            <div>
              {country === Country.Russia ? (
                <Translation
                  tagName="span"
                  {...TXTWCL_Your_N0_account_can_only_be_us_ELL_1}
                  values={{
                    '0': userId,
                    '1': client,
                    '2': userSite
                  }}
                />
              ) : (
                <Translation
                  tagName="span"
                  {...TXTWCL_Your_N0_account_can_only_be_us_ELL}
                  values={{
                    '0': userId,
                    '1': userSite,
                    '2': domain
                  }}
                />
              )}
            </div>
          </StarsInfo>
          <Button
            block
            onClick={() => userSite && navigateToUserSite(userSite)}
            translation={TXTWCL_Visit_N0}
            values={{ '0': userSite }}
          />
        </>
      );
    case statusCode === StatusCode.PREVENT_LOGIN:
    case errorCode === ErrorCode.PREVENT_LOGIN:
      if (site === Sites.CZE) {
        return (
          <StarsInfo type="error">
            <Translation {...TXTWCL_We_are_unable_to_log_you_into__ELL} />
          </StarsInfo>
        );
      }
      if (site === Sites.FRA) {
        return (
          <p className={styles.error}>
            <Translation
              {...TXTWCL_Your_XXAccountBrandXX_Account__ELL_4}
              values={{
                a: (msg: React.ReactNode) => (
                  <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
                )
              }}
            />
          </p>
        );
      }
      return (
        <StarsInfo type="error">
          <Translation {...TXTCLI_Something_went_wrongX} />{' '}
          <Translation {...TXTCLI_We_apologize_for_any_inconveni_ELL_1} />
        </StarsInfo>
      );
    case statusCode === StatusCode.ERR_COUNTRY_RESTRICTED:
    case errorCode === ErrorCode.ERR_COUNTRY_RESTRICTED:
      return (
        <p className={styles.error}>
          <Translation {...TXTWCL_The_XXappXdomainXX_client_is_n_ELL} />
        </p>
      );
    case statusCode === StatusCode.VALIDATION_ERROR:
    case errorCode === ErrorCode.VALIDATION_ERROR:
    case errorCode === ErrorCode.NO_SUCH_USER:
    case serverMessage === 'NO_SUCH_USER':
      return (
        <p className={styles.error}>
          <Translation {...TXTCLI_No_Account_Found} />
        </p>
      );
    case site === Sites.DE && errorCode === ErrorCode.PERMANENTLY_EXCLUDED:
      return (
        <p className={styles.error}>
          <Translation {...TXTWCL_Your_account_has_been_closedX__ELL} />
        </p>
      );
    case errorCode === ErrorCode.PERMANENTLY_EXCLUDED:
      return (
        <p className={styles.error}>
          <Translation
            {...TXTWCL_Your_XXAccountBrandXX_Account__ELL_4}
            values={{
              a: (msg: React.ReactNode) => (
                <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
              )
            }}
          />
        </p>
      );
    // TODO: refactor this in scope of https://jira.pyrsoftware.ca/jira/browse/SW-3300
    case typeof serverMessage === 'string' && serverMessage !== '':
      return (
        <p className={styles.error}>
          <ServerMessageHandler message={serverMessage as string} />
        </p>
      );
    default:
      return (
        <p className={styles.error}>
          <Translation {...TXTMOB_Something_went_wrongX_Please_t_ELL} />
        </p>
      );
  }
};

export default ErrorMessage;
